import type {
  AppointmentBlockedCounts,
  AppointmentBlockedCountsByCreator,
} from "@/features/clinic/types/appointment";
import type { Response } from "@/types/pagination";

import { useQuery } from "@tanstack/react-query";

import { appointmentKeys } from "./keys";

import { useRoles } from "@/hooks/useRoles";
import { api } from "@/lib/api-client";
import { isMockEnabled, simulateDelay } from "@/mock/config";
import { getBlockedCountsMock } from "@/mock/handlers/clinics/appointments";
import { useGlobalStore } from "@/stores/global";

const EMPTY_COUNTS: AppointmentBlockedCounts = {
  staleStatus: 0,
  paymentMissing: 0,
  waitingOnUser: 0,
  missingDetails: 0,
  crmPushFailed: 0,
};

export const EMPTY_BLOCKED_COUNTS: AppointmentBlockedCountsByCreator = {
  ...EMPTY_COUNTS,
  // Copies, not the same object three times: a caller that tallies into one
  // bucket must not find it in the others.
  mine: { ...EMPTY_COUNTS },
  ai: { ...EMPTY_COUNTS },
};

/**
 * The worklist badge numbers, counted server-side rather than derived from a
 * fetched list - a count that is a by-product of a page of rows is only ever
 * as big as that page.
 */
export const useGetBlockedCounts = () => {
  const { activeWorkspaceId } = useGlobalStore();
  const canView = useRoles().can("appointments.view");

  return useQuery({
    queryKey: appointmentKeys.blockedCounts(activeWorkspaceId ?? ""),
    queryFn: async () => {
      if (isMockEnabled()) {
        await simulateDelay();

        return getBlockedCountsMock(activeWorkspaceId ?? "");
      }

      const { data } = await api
        .get("clinics/appointments/blocked-counts")
        .json<Response<AppointmentBlockedCountsByCreator>>();

      return data;
    },
    // Not `initialData`: that seeds the cache as already-successful, and the
    // app disables `refetchOnMount`, so the badges would sit at zero forever.
    // A placeholder leaves the query pending, so it still fetches on mount.
    placeholderData: EMPTY_BLOCKED_COUNTS,
    enabled: !!activeWorkspaceId && canView,
  });
};
